import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, Select, Button } from '@components';
import { CreateAgentData } from '../../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';
import { EmailTemplate, SMSTemplate } from '../../../Communications/types';
import { RecipientsInput } from './RecipientsInput';
import { RoutingRuleBuilder } from './RoutingRuleBuilder';

const SHOW_EMAIL_FOLLOW_UP = false;

interface PostCallTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | boolean | unknown[]
  ) => void;
  onBatchInputChange?: (updates: Partial<CreateAgentData>) => void;
  errors: Record<string, string>;
}

const PostCallTab: React.FC<PostCallTabProps> = ({
  formData,
  onInputChange,
  onBatchInputChange,
  errors,
}) => {
  const { t } = useTranslation();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSMSTemplates] = useState<SMSTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [openSMSVariableDropdown, setOpenSMSVariableDropdown] = useState(false);
  const smsTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Extract variable names from variable_extraction schema
  const extractedVariables = React.useMemo(() => {
    if (!formData.variable_extraction) return [];

    try {
      const parsed = JSON.parse(formData.variable_extraction);
      if (parsed.schema?.properties) {
        return Object.keys(parsed.schema.properties);
      }
    } catch (e) {
      // Invalid JSON, return empty array
    }
    return [];
  }, [formData.variable_extraction]);

  // Always include post-call default variables, remove duplicates
  const allAvailableVariables = React.useMemo(
    () =>
      Array.from(
        new Set([
          'caller_number',
          'agent_name',
          'started_at',
          'ended_at',
          'conversation_summary',
          ...extractedVariables,
        ])
      ),
    [extractedVariables]
  );

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhoneNumber = (phone: string): boolean => {
    // Must start with + followed by country code and number (e.g., +4915140426105)
    return /^\+[1-9]\d{1,14}$/.test(phone);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const [emailResponse, smsResponse] = await Promise.all([
          ApiClient.get('/communications/email-templates/'),
          ApiClient.get('/communications/sms-templates/'),
        ]);

        const emailResult = await handleApiResult<EmailTemplate[]>(
          emailResponse,
          'Failed to fetch email templates'
        );
        const smsResult = await handleApiResult<SMSTemplate[]>(
          smsResponse,
          'Failed to fetch SMS templates'
        );

        if (emailResult.success && emailResult.data) {
          setEmailTemplates(emailResult.data.filter(t => t.status === 'active'));
        }
        if (smsResult.success && smsResult.data) {
          setSMSTemplates(smsResult.data.filter(t => t.status === 'active'));
        }
      } catch (err) {
        handleApiCatch(err, 'Failed to fetch templates');
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleEmailTemplateSelect = (templateId: string) => {
    if (!templateId) {
      onInputChange('email_template', '');
      return;
    }

    const template = emailTemplates.find(t => t.id === templateId);
    if (!template) return;

    if (onBatchInputChange) {
      onBatchInputChange({
        email_template: templateId,
        email_follow_up_subject: template.subject || '',
        email_follow_up_content: template.body || '',
      });
    } else {
      onInputChange('email_template', templateId);
      onInputChange('email_follow_up_subject', template.subject || '');
      onInputChange('email_follow_up_content', template.body || '');
    }
  };

  const handleSMSTemplateSelect = (templateId: string) => {
    if (!templateId) {
      onInputChange('sms_template', '');
      return;
    }

    const template = smsTemplates.find(t => t.id === templateId);
    if (!template) return;

    onInputChange('sms_template', templateId);
    onInputChange('sms_follow_up_content', template.content || '');
  };

  const insertSMSVariableAtCursor = (variableName: string) => {
    const textarea = smsTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const variableToken = `{{ ${variableName} }}`;

    const newValue = currentValue.substring(0, start) + variableToken + currentValue.substring(end);

    onInputChange('sms_follow_up_content', newValue);

    // Set cursor position after inserted variable
    setTimeout(() => {
      const newCursorPos = start + variableToken.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);

    setOpenSMSVariableDropdown(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.postCall')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.postCallSettingsDescription')}
        </p>
      </div>

      <div className="space-y-8">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              summarize
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.conversationProcessing')}
            </h4>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="intelligent-conversation-summary"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.intelligentConversationSummary')}
              </label>
              <Select
                id="intelligent-conversation-summary"
                value={formData.intelligent_conversation_summary || 'no'}
                onChange={value =>
                  onInputChange(
                    'intelligent_conversation_summary',
                    Array.isArray(value) ? value[0] : value
                  )
                }
                options={[
                  {
                    value: 'no',
                    label: t('agents.summaryOptionNoLabel'),
                    description: t('agents.summaryOptionNoDescription'),
                    icon: <span className="material-icons text-gray-400">block</span>,
                  },
                  {
                    value: 'yes_without_notification',
                    label: t('agents.summaryOptionSaveOnlyLabel'),
                    description: t('agents.summaryOptionSaveOnlyDescription'),
                    icon: <span className="material-icons text-blue-500">save</span>,
                  },
                  {
                    value: 'yes_with_email',
                    label: t('agents.summaryOptionEmailLabel'),
                    description: t('agents.summaryOptionEmailDescription'),
                    icon: <span className="material-icons text-primary-500">email</span>,
                  },
                  {
                    value: 'yes_with_sms',
                    label: t('agents.summaryOptionSmsLabel'),
                    description: t('agents.summaryOptionSmsDescription'),
                    icon: <span className="material-icons text-green-500">sms</span>,
                  },
                  {
                    value: 'yes_with_email_and_sms',
                    label: t('agents.summaryOptionBothLabel'),
                    description: t('agents.summaryOptionBothDescription'),
                    icon: <span className="material-icons text-purple-500">send</span>,
                  },
                ]}
                className="w-full"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.generateAiSummaries')}
              </p>
            </div>
          </div>

          {(formData.intelligent_conversation_summary === 'yes_with_email' ||
            formData.intelligent_conversation_summary === 'yes_with_email_and_sms') && (
            <>
              <div className="mt-4">
                <div className="mb-2">
                  <div className="flex items-center">
                    <span className="material-icons text-gray-500 dark:text-gray-400 mr-2 text-sm">
                      alt_route
                    </span>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('agents.summaryRoutingPrompt')}
                    </label>
                  </div>
                </div>
                <RoutingRuleBuilder
                  value={formData.summary_routing_prompt || ''}
                  onChange={value => onInputChange('summary_routing_prompt', value)}
                  helperText={t('agents.summaryRoutingPromptHint')}
                />
              </div>

              <div className="mt-6">
                <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                  <div className="flex items-center mb-3">
                    <span className="material-icons text-gray-500 dark:text-gray-400 mr-2 text-sm">
                      email
                    </span>
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {t('agents.fallbackEmailRecipients')}
                    </h5>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {t('agents.fallbackEmailRecipientsDescription')}
                  </p>
                  <RecipientsInput
                    id="summary-email-recipients"
                    label=""
                    placeholder={t('agents.summaryEmailRecipientsHint')}
                    hint=""
                    recipients={formData.summary_email_recipients || []}
                    onRecipientsChange={recipients =>
                      onInputChange('summary_email_recipients', recipients)
                    }
                    validate={isValidEmail}
                    inputType="email"
                  />
                </div>
              </div>

              <div className="mt-6">
                <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                  <div className="flex items-center">
                    <input
                      id="summary-include-transcript"
                      type="checkbox"
                      checked={formData.summary_include_transcript ?? false}
                      onChange={e =>
                        onInputChange('summary_include_transcript', e.target.checked)
                      }
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="summary-include-transcript"
                      className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('agents.summaryIncludeTranscript')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                    {t('agents.summaryIncludeTranscriptHint')}
                  </p>
                </div>
              </div>
            </>
          )}

          {(formData.intelligent_conversation_summary === 'yes_with_sms' ||
            formData.intelligent_conversation_summary === 'yes_with_email_and_sms') && (
            <div className="mt-4">
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <div className="flex items-center mb-3">
                  <span className="material-icons text-gray-500 dark:text-gray-400 mr-2 text-sm">
                    sms
                  </span>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('agents.summarySmsRecipients')}
                  </h5>
                </div>
                <RecipientsInput
                  id="summary-sms-recipients"
                  label=""
                  placeholder={t('agents.summarySmsRecipientsHint')}
                  hint={t('agents.summarySmsRecipientsHint')}
                  recipients={formData.summary_sms_recipients || []}
                  onRecipientsChange={recipients =>
                    onInputChange('summary_sms_recipients', recipients)
                  }
                  validate={isValidPhoneNumber}
                  inputType="tel"
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              reply
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.followUpEnabled')}
            </h4>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="follow-up-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('common.status')}
              </label>
              <Select
                id="follow-up-status"
                value={formData.follow_up_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const booleanValue = selectedValue === 'enabled';
                  onInputChange('follow_up_enabled', booleanValue);

                  if (!booleanValue && onBatchInputChange) {
                    onBatchInputChange({
                      email_template: null,
                      email_follow_up_subject: '',
                      email_follow_up_content: '',
                      email_follow_up_timing: 'immediately',
                      email_follow_up_recipients: [],
                      sms_template: null,
                      sms_follow_up_content: '',
                      sms_follow_up_timing: 'immediately',
                      sms_follow_up_recipients: [],
                      sms_follow_up_mode: 'template',
                      sms_follow_up_prompt: '',
                    });
                  }
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
                error={errors.follow_up_enabled ? true : undefined}
                errorText={errors.follow_up_enabled}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('agents.sendAutomatedFollowUp')}
              </p>
            </div>
          </div>

          {formData.follow_up_enabled && (
            <div className="mt-6 space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                  message
                </span>
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.communicationTemplates')}
                </h5>
              </div>

              {SHOW_EMAIL_FOLLOW_UP && (
                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center mb-4">
                    <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                      email
                    </span>
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {t('agents.emailFollowUp')}
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="email-template"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('agents.emailTemplate')}
                      </label>
                      <Select
                        id="email-template"
                        value={formData.email_template || ''}
                        onChange={value =>
                          handleEmailTemplateSelect(Array.isArray(value) ? value[0] : value)
                        }
                        options={[
                          { value: '', label: t('agents.selectEmailTemplate') },
                          ...emailTemplates.map(template => ({
                            value: template.id,
                            label: template.title,
                          })),
                        ]}
                        className="w-full"
                        disabled={templatesLoading}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email-timing"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('agents.emailSendTiming')}
                      </label>
                      <Select
                        id="email-timing"
                        value={formData.email_follow_up_timing || 'immediately'}
                        onChange={value =>
                          onInputChange(
                            'email_follow_up_timing',
                            Array.isArray(value) ? value[0] : value
                          )
                        }
                        options={[
                          { value: 'immediately', label: t('agents.immediatelyAfterCall') },
                          { value: '1_hour', label: t('agents.1HourAfterCall') },
                          { value: '24_hours', label: t('agents.24HoursAfterCall') },
                          { value: '3_days', label: t('agents.3DaysAfterCall') },
                          { value: '1_week', label: t('agents.1WeekAfterCall') },
                        ]}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label
                      htmlFor="email-subject"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.emailSubject')}
                    </label>
                    <Input
                      id="email-subject"
                      type="text"
                      className="w-full"
                      value={formData.email_follow_up_subject || ''}
                      onChange={e => onInputChange('email_follow_up_subject', e.target.value)}
                      placeholder={t('agents.emailSubjectPlaceholder')}
                    />
                  </div>

                  <div className="mt-6">
                    <label
                      htmlFor="email-content"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.emailContent')}
                    </label>
                    <Textarea
                      id="email-content"
                      className="w-full"
                      rows={6}
                      value={formData.email_follow_up_content || ''}
                      onChange={e => onInputChange('email_follow_up_content', e.target.value)}
                      placeholder={t('agents.emailContentPlaceholder')}
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('agents.useVariableNameForDynamicContent')}
                    </p>
                  </div>

                  <RecipientsInput
                    id="email-recipients"
                    label={t('agents.emailRecipients')}
                    placeholder={t('agents.emailRecipientsHint')}
                    hint={t('agents.emailRecipientsHint')}
                    recipients={formData.email_follow_up_recipients || []}
                    onRecipientsChange={recipients =>
                      onInputChange('email_follow_up_recipients', recipients)
                    }
                    validate={isValidEmail}
                    inputType="email"
                  />
                </div>
              )}

              <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center mb-4">
                  <span className="material-icons text-primary-500 dark:text-primary-300 mr-2 text-sm">
                    sms
                  </span>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('agents.smsFollowUp')}
                  </h5>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="sms-template"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.smsTemplate')}
                    </label>
                    <Select
                      id="sms-template"
                      value={formData.sms_template || ''}
                      onChange={value =>
                        handleSMSTemplateSelect(Array.isArray(value) ? value[0] : value)
                      }
                      options={[
                        { value: '', label: t('agents.selectSmsTemplate') },
                        ...smsTemplates.map(template => ({
                          value: template.id,
                          label: template.title,
                        })),
                      ]}
                      className="w-full"
                      disabled={templatesLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="sms-timing"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.smsSendTiming')}
                    </label>
                    <Select
                      id="sms-timing"
                      value={formData.sms_follow_up_timing || 'immediately'}
                      onChange={value =>
                        onInputChange(
                          'sms_follow_up_timing',
                          Array.isArray(value) ? value[0] : value
                        )
                      }
                      options={[
                        { value: 'immediately', label: t('agents.immediatelyAfterCall') },
                        { value: '1_hour', label: t('agents.1HourAfterCall') },
                        { value: '24_hours', label: t('agents.24HoursAfterCall') },
                        { value: '3_days', label: t('agents.3DaysAfterCall') },
                        { value: '1_week', label: t('agents.1WeekAfterCall') },
                      ]}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label
                    htmlFor="sms-mode"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.smsFollowUpMode')}
                  </label>
                  <Select
                    id="sms-mode"
                    value={formData.sms_follow_up_mode || 'template'}
                    onChange={value =>
                      onInputChange(
                        'sms_follow_up_mode',
                        Array.isArray(value) ? value[0] : value
                      )
                    }
                    options={[
                      { value: 'template', label: t('agents.smsModeTemplate') },
                      { value: 'prompt', label: t('agents.smsModePrompt') },
                    ]}
                    className="w-full"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('agents.smsFollowUpModeHint')}
                  </p>
                </div>

                {formData.sms_follow_up_mode === 'prompt' && (
                  <div className="mt-6">
                    <label
                      htmlFor="sms-prompt"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.smsFollowUpPrompt')}
                    </label>
                    <Textarea
                      id="sms-prompt"
                      className="w-full"
                      rows={4}
                      value={formData.sms_follow_up_prompt || ''}
                      onChange={e =>
                        onInputChange('sms_follow_up_prompt', e.target.value)
                      }
                      placeholder={t('agents.smsFollowUpPromptPlaceholder')}
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('agents.smsFollowUpPromptHint')}
                    </p>
                  </div>
                )}

                {formData.sms_follow_up_mode !== 'prompt' && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="sms-content"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('agents.smsContent')}
                    </label>
                    {allAvailableVariables.length > 0 && (
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOpenSMSVariableDropdown(!openSMSVariableDropdown)}
                        >
                          <span className="material-icons text-sm mr-1">add_circle</span>
                          {t('agents.insertVariable')}
                        </Button>
                        {openSMSVariableDropdown && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenSMSVariableDropdown(false)}
                              onKeyDown={e =>
                                e.key === 'Escape' && setOpenSMSVariableDropdown(false)
                              }
                              role="button"
                              tabIndex={0}
                              aria-label="Close dropdown"
                            />
                            <div className="absolute right-0 mt-1 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                              <div className="py-1">
                                {allAvailableVariables.map(variable => {
                                  const translationKey = `agents.variables.${variable}`;
                                  const hasTranslation = t(translationKey) !== translationKey;
                                  const displayName = hasTranslation ? t(translationKey) : variable;

                                  return (
                                    <button
                                      key={variable}
                                      onClick={() => insertSMSVariableAtCursor(variable)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                                    >
                                      <span>{displayName}</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                        {`{{ ${variable} }}`}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <Textarea
                    ref={el => {
                      smsTextareaRef.current = el;
                    }}
                    id="sms-content"
                    className="w-full"
                    rows={4}
                    maxLength={160}
                    value={formData.sms_follow_up_content || ''}
                    onChange={e => onInputChange('sms_follow_up_content', e.target.value)}
                    placeholder={t('agents.smsContentPlaceholder')}
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('agents.smsLimitedTo160Characters')}
                  </p>
                </div>
                )}

                <div className="mt-6">
                  <RecipientsInput
                    id="sms-recipients"
                    label={t('agents.smsRecipients')}
                    placeholder={t('agents.smsRecipientsHint')}
                    hint={t('agents.smsRecipientsHint')}
                    recipients={formData.sms_follow_up_recipients || []}
                    onRecipientsChange={recipients =>
                      onInputChange('sms_follow_up_recipients', recipients)
                    }
                    validate={isValidPhoneNumber}
                    inputType="tel"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PostCallTab };
